import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, ChevronRight, LogIn, Cloud, CloudOff, RefreshCw, AlertTriangle, Settings, Globe, Plus, Check, Shield } from 'lucide-react';
import { useAuth } from '../../features/auth/context/AuthContext';
import Login from '../../features/auth/components/Login';
import SettingsMenu from './SettingsMenu';
import TimelineSwitcher from './TimelineSwitcher';
import syncManager, { SyncStatus, SyncState } from '../../features/sync/services/syncManager';

interface TimelineItem {
  id: string;
  name: string;
  ownerId?: string;
  isLocal?: boolean;
  isLocalSubscription?: boolean;
  isSubscribed?: boolean;
  color?: string | null;
}

interface TimelineData {
  id?: string;
  name?: string;
  schedule?: unknown;
  elements?: unknown[];
  isPublic?: boolean;
  publicCode?: string | null;
  [key: string]: unknown;
}

interface TopNavProps {
  timelineName?: string;
  sessionName?: string;
  onTimelineNameChange?: (name: string) => void;
  onSessionNameChange?: (name: string) => void;
  isPublic?: boolean;
  onTogglePublic?: () => void;
  publicCode?: string;
  onPublicCodeChange?: (code: string) => void;
  onSetDefaults?: () => void;
  onLoadDefaults?: () => void;
  isOwner?: boolean;
  timelineData?: TimelineData;
  sessionData?: TimelineData;
  allTimelines?: TimelineItem[];
  allSessions?: TimelineItem[];
  currentTimelineId?: string | null;
  currentSessionId?: string | null;
  onTimelineSwitch?: (timelineId: string) => void;
  onSessionSwitch?: (sessionId: string) => void;
  onCreateNewTimeline?: () => void;
  onCreateNewSession?: () => void;
  onDeleteTimeline?: () => void;
  onDeleteSession?: () => void;
  canDelete?: boolean;
  timelineId?: string;
  sessionId?: string;
  isAutoAdvanceEnabled?: boolean;
  onAutoAdvanceChange?: (enabled: boolean) => void;
  autoAdvanceBuffer?: number;
  onAutoAdvanceBufferChange?: (buffer: number) => void;
  timeOffset?: number;
  onTimeOffsetChange?: (offset: number) => void;
  onUnsubscribe?: (timeline: TimelineItem) => void;
  onSubscribe?: () => void;
  isSubscribed?: boolean;
}

const TopNav: React.FC<TopNavProps> = ({
  timelineName,
  sessionName,
  onTimelineNameChange,
  onSessionNameChange,
  isPublic,
  onTogglePublic,
  publicCode,
  onPublicCodeChange,
  onSetDefaults,
  onLoadDefaults,
  isOwner,
  timelineData,
  sessionData,
  allTimelines = [],
  allSessions = [],
  currentTimelineId,
  currentSessionId,
  onTimelineSwitch,
  onSessionSwitch,
  onCreateNewTimeline,
  onCreateNewSession,
  onDeleteTimeline,
  onDeleteSession,
  canDelete,
  timelineId,
  sessionId,
  isAutoAdvanceEnabled,
  onAutoAdvanceChange,
  autoAdvanceBuffer,
  onAutoAdvanceBufferChange,
  timeOffset,
  onTimeOffsetChange,
  onUnsubscribe,
  onSubscribe,
  isSubscribed,
}) => {
  // Support both new and legacy prop names
  const effectiveTimelineName = timelineName || sessionName;
  const effectiveOnTimelineNameChange = onTimelineNameChange || onSessionNameChange;
  const effectiveTimelineData = timelineData || sessionData;
  const effectiveAllTimelines = allTimelines.length > 0 ? allTimelines : allSessions;
  const effectiveCurrentTimelineId = currentTimelineId || currentSessionId;
  const effectiveOnTimelineSwitch = onTimelineSwitch || onSessionSwitch;
  const effectiveOnCreateNewTimeline = onCreateNewTimeline || onCreateNewSession;
  const effectiveOnDeleteTimeline = onDeleteTimeline || onDeleteSession;
  const effectiveTimelineId = timelineId || sessionId;

  const { currentUser, logout } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [syncState, setSyncState] = useState<SyncState>(() => ({
    status: SyncStatus.SYNCED,
    pendingCount: 0,
    isOnline: navigator.onLine,
    isWebSocketConnected: false,
    isSyncing: false,
    recentConflicts: [],
    lastSyncTime: null,
  }));

  const userMenuRef = useRef<HTMLDivElement>(null);

  // Subscribe to sync manager state
  useEffect(() => {
    const unsubscribe = syncManager.subscribe(setSyncState);
    return unsubscribe;
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const displayName = currentUser?.firstName && currentUser?.lastName
    ? `${currentUser.firstName} ${currentUser.lastName}`
    : currentUser?.displayName || currentUser?.email?.split('@')[0] || 'User';

  // Get user initials for avatar
  const getInitials = (): string => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName[0]}${currentUser.lastName[0]}`.toUpperCase();
    }
    if (currentUser?.displayName) {
      const names = currentUser.displayName.split(' ');
      if (names.length >= 2) {
        return `${names[0][0]}${names[1][0]}`.toUpperCase();
      }
      return currentUser.displayName.substring(0, 2).toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Render sync status indicator
  const renderSyncStatus = () => {
    const { status, pendingCount, recentConflicts } = syncState;

    // Only show when logged in
    if (!currentUser) return null;

    let icon: React.ReactNode;
    let color: string;
    let title: string;

    switch (status) {
      case SyncStatus.OFFLINE:
        icon = <CloudOff className="w-4 h-4" />;
        color = 'text-gray-500';
        title = 'Offline - changes will sync when back online';
        break;
      case SyncStatus.DISCONNECTED:
        icon = <CloudOff className="w-4 h-4" />;
        color = 'text-orange-400';
        title = 'Disconnected - real-time updates paused';
        break;
      case SyncStatus.SYNCING:
        icon = <RefreshCw className="w-4 h-4 animate-spin" />;
        color = 'text-blue-400';
        title = 'Syncing...';
        break;
      case SyncStatus.PENDING:
        icon = (
          <div className="relative">
            <Cloud className="w-4 h-4" />
            <span className="absolute -top-1 -right-1 bg-yellow-500 text-[10px] text-white rounded-full w-3.5 h-3.5 flex items-center justify-center font-bold">
              {pendingCount > 9 ? '9+' : pendingCount}
            </span>
          </div>
        );
        color = 'text-yellow-400';
        title = `${pendingCount} change${pendingCount > 1 ? 's' : ''} pending`;
        break;
      case SyncStatus.CONFLICT:
        icon = <AlertTriangle className="w-4 h-4" />;
        color = 'text-orange-400';
        title = `${recentConflicts.length} conflict${recentConflicts.length > 1 ? 's' : ''} detected`;
        break;
      case SyncStatus.SYNCED:
      default:
        icon = <Cloud className="w-4 h-4" />;
        color = 'text-green-400';
        title = 'All changes synced';
        break;
    }

    return (
      <button
        onClick={() => {
          if (status === SyncStatus.PENDING || status === SyncStatus.CONFLICT) {
            syncManager.sync();
          }
        }}
        className={`p-1.5 rounded-lg transition-colors ${color} hover:bg-gray-200/50 dark:hover:bg-gray-700/50`}
        title={title}
      >
        {icon}
      </button>
    );
  };

  return (
    <>
      <nav className="relative z-[1000] bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border-b border-gray-300 dark:border-gray-700 mb-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Left side: Hamburger menu */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={() => setShowSettingsMenu(true)}
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                title="Open settings"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>

            {/* Center: Timeline name + switcher + public indicator */}
            <div className="flex items-center gap-2 min-w-0 justify-center flex-1">
              {/* Public indicator */}
              {isPublic && (
                <div className="flex items-center gap-1 px-2 py-1 bg-emerald-600/20 border border-emerald-500/30 rounded-full">
                  <Globe className="w-3 h-3 text-emerald-400" />
                  <span className="text-xs text-emerald-400 font-medium">Public</span>
                </div>
              )}
              {/* Subscribe button for authenticated non-owners viewing public timelines */}
              {isPublic && !isOwner && currentUser && onSubscribe && (
                isSubscribed ? (
                  <div className="flex items-center gap-1 px-2 py-1 bg-blue-600/20 border border-blue-500/30 rounded-full">
                    <Check className="w-3 h-3 text-blue-400" />
                    <span className="text-xs text-blue-400 font-medium">Subscribed</span>
                  </div>
                ) : (
                  <button
                    onClick={onSubscribe}
                    className="flex items-center gap-1 px-2 py-1 bg-blue-600/20 border border-blue-500/30 rounded-full hover:bg-blue-600/30 transition-colors"
                  >
                    <Plus className="w-3 h-3 text-blue-400" />
                    <span className="text-xs text-blue-400 font-medium">Subscribe</span>
                  </button>
                )
              )}
              {/* Timeline title + switcher. The whole "Name ›" is one clickable button so
                  the switcher is discoverable; falls back to a plain heading if no switcher. */}
              {effectiveOnTimelineSwitch ? (
                <TimelineSwitcher
                  title={effectiveTimelineName}
                  timelines={effectiveAllTimelines}
                  currentTimelineId={effectiveCurrentTimelineId}
                  currentUserId={currentUser?.id}
                  onTimelineSwitch={effectiveOnTimelineSwitch}
                  onCreateNew={currentUser ? effectiveOnCreateNewTimeline : undefined}
                  onUnsubscribe={onUnsubscribe}
                  showLoginPrompt={!currentUser}
                  onLoginClick={() => setShowLoginModal(true)}
                />
              ) : (
                <h1
                  className="text-gray-900 dark:text-white text-base font-medium truncate max-w-[200px]"
                  title={effectiveTimelineName}
                >
                  {effectiveTimelineName || 'Untitled Timeline'}
                </h1>
              )}
            </div>

            {/* Right side: Sync status + User menu */}
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* Sync status indicator */}
              {renderSyncStatus()}

              {!currentUser ? (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="flex items-center gap-1.5 text-blue-400 hover:text-blue-300 transition-colors text-sm font-medium"
                >
                  <LogIn className="w-4 h-4" />
                  <span>Login</span>
                </button>
              ) : (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 transition-colors text-sm font-medium group"
                  >
                    <span>{displayName}</span>
                    <ChevronRight
                      className={`w-4 h-4 text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-all duration-200 ${
                        showUserMenu ? 'rotate-90' : ''
                      }`}
                    />
                  </button>

                  {/* User dropdown menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[9999] overflow-hidden">
                      {/* User profile section */}
                      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3">
                          {/* Avatar */}
                          {currentUser?.profileImage ? (
                            <img
                              src={currentUser.profileImage}
                              alt={displayName}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                              {getInitials()}
                            </div>
                          )}
                          {/* Name and email */}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {displayName}
                            </div>
                            {currentUser?.email && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {currentUser.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Menu items */}
                      <div className="py-1">
                        <Link
                          to="/account"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          <Settings className="w-4 h-4" />
                          Account Settings
                        </Link>
                        {currentUser?.isAdmin && (
                          <Link
                            to="/admin"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            <Shield className="w-4 h-4" />
                            Admin Portal
                          </Link>
                        )}
                        <button
                          onClick={handleLogout}
                          className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        >
                          Logout
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      {showLoginModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLoginModal(false)}
        >
          <div className="max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <Login onClose={() => setShowLoginModal(false)} />
          </div>
        </div>
      )}

      {/* Settings Menu Slide-out */}
      <SettingsMenu
        isOpen={showSettingsMenu}
        onClose={() => setShowSettingsMenu(false)}
        isPublic={isPublic}
        onTogglePublic={onTogglePublic}
        publicCode={publicCode}
        onPublicCodeChange={onPublicCodeChange}
        onSetDefaults={onSetDefaults}
        onLoadDefaults={onLoadDefaults}
        isOwner={isOwner}
        timelineData={effectiveTimelineData}
        timelineId={effectiveTimelineId}
        onDeleteTimeline={effectiveOnDeleteTimeline}
        canDelete={canDelete}
        timelineName={effectiveTimelineName}
        onTimelineNameChange={effectiveOnTimelineNameChange}
        isAutoAdvanceEnabled={isAutoAdvanceEnabled}
        onAutoAdvanceChange={onAutoAdvanceChange}
        autoAdvanceBuffer={autoAdvanceBuffer}
        onAutoAdvanceBufferChange={onAutoAdvanceBufferChange}
        timeOffset={timeOffset}
        onTimeOffsetChange={onTimeOffsetChange}
      />
    </>
  );
};

export default TopNav;
